import { useEffect, useMemo, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { TrendingUp, Calendar, Download, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const COLORS = ['#C97A5E', '#5C4033', '#D2B48C', '#4A7c59', '#2A9D8F', '#E76F51'];

export default function ReportsSalesPage() {
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [salesData, setSalesData] = useState(null);
  const [chartKey, setChartKey] = useState(0);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  useEffect(() => {
    fetchSalesReport();
  }, [period]);

  useEffect(() => {
    setChartKey((prev) => prev + 1);
  }, [salesData]);

  const fetchSalesReport = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getSalesSummary(period);
      setSalesData(response.data);
    } catch (error) {
      toast.error('Failed to fetch sales report');
    } finally {
      setLoading(false);
    }
  };

  const salesDailyData = useMemo(
    () => (salesData?.daily_data || []).map((item) => ({
      ...item,
      orders: Number(item.orders || 0),
      order_amount: Number(item.order_amount || 0),
    })),
    [salesData]
  );

  const salesStatusData = useMemo(
    () => Object.entries(salesData?.status_breakdown || {}).map(([name, value]) => ({
      name,
      value: Number(value || 0),
    })),
    [salesData]
  );

  const salesChannelData = useMemo(
    () => Object.entries(salesData?.channel_breakdown || {}).map(([name, value]) => ({
      name,
      value: Number(value || 0),
    })),
    [salesData]
  );

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const res = await adminApi.downloadAdminDatasetExcel('sales-overview', { period });
      downloadBlob(res.data, 'sales-overview.xlsx');
    } catch (error) {
      toast.error('Failed to download Excel');
    } finally {
      setDownloadingExcel(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-sales-page">
      <LoadingOverlay open={downloadingExcel} message="Please wait. Report is being generated" />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Reports · Sales Overview</h1>
          <p className="text-[#A8A8A8] mt-1">Orders, revenue, and delivery trends</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleDownloadExcel}>
            <Download className="w-4 h-4 mr-2" /> Download Excel
          </Button>
          <Calendar className="w-5 h-5 text-[#A8A8A8]" />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40 border-[#E5E5E5]" data-testid="period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Total Orders</p>
            <p className="text-2xl font-bold text-[#2D2420]">{salesData?.total_orders || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Order Value</p>
            <p className="text-2xl font-bold text-[#2D2420]">{formatCurrency(salesData?.total_order_amount)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Collected</p>
            <p className="text-2xl font-bold text-[#4A7c59]">{formatCurrency(salesData?.total_payments_collected)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Delivered</p>
            <p className="text-2xl font-bold text-[#C97A5E]">{salesData?.status_breakdown?.delivered || 0}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="text-lg text-[#2D2420]">Daily Orders & Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart key={`sales-daily-${chartKey}`} data={salesDailyData.slice(-14)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip
                    formatter={(value, name) => [name === 'order_amount' ? formatCurrency(value) : value, name === 'order_amount' ? 'Revenue' : 'Orders']}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Line type="monotone" dataKey="orders" stroke="#C97A5E" strokeWidth={2} dot={{ fill: '#C97A5E' }} />
                  <Line type="monotone" dataKey="order_amount" stroke="#5C4033" strokeWidth={2} dot={{ fill: '#5C4033' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="text-lg text-[#2D2420]">Order Status Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={salesStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {salesStatusData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Orders by Channel</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart key={`sales-channel-${chartKey}`} data={salesChannelData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#C97A5E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
